<script setup lang="ts">
// Persistent player (HRTH-164.1). Audio: a bar along the bottom (on phones art, title, play
// and next, with a progress line; tapping it opens Now Playing full screen with a large
// seek bar, previous/play/next and what's up next). Video: an overlay. One media element
// plays throughout; its end moves on to the next in the queue.
import { computed, ref, watch } from 'vue'
import { useBottomBar } from '../lib/bottomBar'
import { useMediaState } from '../lib/mediaState'
import { formatClock } from '../lib/pattern'
import { usePlayer } from '../stores/player'
import { useSession } from '../stores/session'
import Icon from './Icon.vue'
import MediaElement from './MediaElement.vue'

const player = usePlayer()
const cur = computed(() => player.current)
const skip = computed(() => (player.queue.items.length > 1 ? { previous: true, next: player.hasNext } : undefined))
const m = useMediaState(() => player.el)
const bar = ref<HTMLElement | null>(null)
useBottomBar('player', bar)

function onElement(el: HTMLMediaElement): void {
  player.el = el
}
/** A fresh stream of what's playing, when its own stops loading. */
const session = useSession()
const reload = () => session.playLibrary(cur.value!.item)
const onEnded = () => player.ended()
const onTime = () => player.position()
watch(
  () => player.el,
  (el, old) => {
    old?.removeEventListener('ended', onEnded)
    old?.removeEventListener('timeupdate', onTime)
    el?.addEventListener('ended', onEnded)
    el?.addEventListener('timeupdate', onTime)
  },
)
watch(cur, (c) => {
  if (!c) player.el = null
})
</script>

<template>
  <template v-if="cur && cur.item.kind === 'audio'">
    <!-- The bar -->
    <div
      ref="bar"
      class="fixed right-0 bottom-0 left-0 z-30 border-t border-border-strong bg-bg-primary md:left-[60px]"
      data-testid="now-playing"
    >
      <MediaElement :key="cur.play.url" :play="cur.play" kind="audio" autoplay bare :reload="reload" @element="onElement" />
      <!-- Phones: progress along the top edge -->
      <div class="absolute inset-x-0 -top-px h-0.5 bg-border md:hidden" aria-hidden="true">
        <div class="h-full bg-ink" :style="{ width: m.pct(m.shown.value) }" />
      </div>
      <div class="flex h-16 items-center gap-3 px-3 md:grid md:h-20 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] md:gap-6 md:px-4">
        <!-- What's playing; on phones the way into Now Playing -->
        <button
          class="flex min-w-0 flex-1 items-center gap-3 text-left md:pointer-events-none"
          aria-label="Open Now Playing"
          data-testid="now-playing-open"
          @click="player.expanded = true"
        >
          <span class="field-grid flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden border border-border text-text-muted md:h-12 md:w-12">
            <img v-if="player.art" :src="player.art" alt="" class="h-full w-full object-cover" />
            <span v-else class="mono">♪</span>
          </span>
          <span class="min-w-0">
            <span class="block truncate text-sm" data-testid="now-playing-title">{{ cur.item.title }}</span>
            <span class="mono block truncate text-text-muted">{{ cur.item.artist ?? 'Unknown artist' }}</span>
          </span>
        </button>

        <!-- Transport and seek (the seek row only on wider screens) -->
        <div class="flex shrink-0 flex-col items-center gap-0.5 md:min-w-0">
          <div class="flex items-center gap-1 md:gap-3">
            <button class="player-btn max-md:hidden" aria-label="Previous" data-testid="player-previous" @click="player.previous()">
              <Icon name="previous" />
            </button>
            <button
              class="player-btn player-btn-main"
              :aria-label="m.playing.value ? 'Pause' : 'Play'"
              data-testid="player-toggle"
              @click="m.toggle()"
            >
              <Icon :name="m.playing.value ? 'pause' : 'play'" :size="5" />
            </button>
            <button class="player-btn" aria-label="Next" :disabled="!player.hasNext" data-testid="player-next" @click="player.next()">
              <Icon name="next" />
            </button>
          </div>
          <div class="mono flex w-full items-center gap-2 text-text-muted max-md:hidden">
            <span class="w-12 shrink-0 text-right tabular-nums" data-testid="media-time">{{ formatClock(m.shown.value) }}</span>
            <input
              type="range"
              class="media-range min-w-0 flex-1 text-text-primary"
              min="0"
              :max="m.known.value ? m.duration.value : 0"
              step="any"
              :value="m.shown.value"
              :disabled="!m.known.value"
              :style="{ '--played': m.pct(m.shown.value), '--buffered': m.pct(m.buffered.value) }"
              aria-label="Seek"
              data-testid="media-seek"
              @input="m.scrub"
              @change="m.seek"
            />
            <span class="w-12 shrink-0 tabular-nums" data-testid="media-duration">{{ m.known.value ? formatClock(m.duration.value) : '--:--' }}</span>
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 max-md:hidden">
          <span v-if="player.upNext.length" class="mono text-text-muted" :title="player.upNext.map((i) => i.title).join('\n')" data-testid="player-up-next-count">
            +{{ player.upNext.length }} next
          </span>
          <button class="player-btn" :aria-label="m.muted.value ? 'Unmute' : 'Mute'" @click="m.toggleMute()">
            <svg viewBox="0 0 12 12" class="h-3.5 w-3.5" aria-hidden="true">
              <path d="M1 4.5h2.5L6.5 2v8L3.5 7.5H1z" fill="currentColor" />
              <path v-if="m.muted.value || m.volume.value === 0" d="M8 4.5l3 3M11 4.5l-3 3" stroke="currentColor" stroke-width="1.2" />
              <path v-else d="M8.2 4.2a2.5 2.5 0 0 1 0 3.6" fill="none" stroke="currentColor" stroke-width="1.2" />
            </svg>
          </button>
          <input
            type="range"
            class="media-range w-24"
            min="0"
            max="1"
            step="0.05"
            :value="m.muted.value ? 0 : m.volume.value"
            :style="{ '--played': `${(m.muted.value ? 0 : m.volume.value) * 100}%`, '--buffered': '0%' }"
            aria-label="Volume"
            @input="m.setVolume"
          />
          <button class="player-btn" aria-label="Close player" data-testid="player-close" @click="player.stop()">
            <Icon name="close" />
          </button>
        </div>
      </div>
    </div>

    <!-- Phones: Now Playing, full screen -->
    <div
      v-if="player.expanded"
      class="fixed inset-0 z-40 flex flex-col bg-bg-primary md:hidden"
      role="dialog"
      aria-label="Now Playing"
      data-testid="now-playing-full"
    >
      <div class="flex h-14 shrink-0 items-center justify-between px-2">
        <button class="player-btn" aria-label="Close Now Playing" data-testid="now-playing-collapse" @click="player.expanded = false">
          <Icon name="down" :size="6" />
        </button>
        <span class="mono tracking-[0.14em] text-text-muted uppercase">Now playing</span>
        <button class="player-btn" aria-label="Stop and close the player" @click="player.stop()">
          <Icon name="close" :size="5" />
        </button>
      </div>
      <div class="scroll-thin flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-6">
        <div class="field-grid mx-auto mt-2 flex aspect-square w-full max-w-sm shrink-0 items-center justify-center overflow-hidden border border-border text-text-muted">
          <img v-if="player.art" :src="player.art" alt="" class="h-full w-full object-cover" />
          <span v-else class="mono text-4xl">♪</span>
        </div>
        <div class="mt-6 min-w-0">
          <div class="truncate text-xl" data-testid="now-playing-full-title">{{ cur.item.title }}</div>
          <div class="truncate text-text-muted">{{ cur.item.artist ?? 'Unknown artist' }}</div>
        </div>
        <input
          type="range"
          class="media-range media-range-lg mt-4 w-full text-text-primary"
          min="0"
          :max="m.known.value ? m.duration.value : 0"
          step="any"
          :value="m.shown.value"
          :disabled="!m.known.value"
          :style="{ '--played': m.pct(m.shown.value), '--buffered': m.pct(m.buffered.value) }"
          aria-label="Seek"
          data-testid="now-playing-seek"
          @input="m.scrub"
          @change="m.seek"
        />
        <div class="mono flex justify-between text-text-muted tabular-nums">
          <span>{{ formatClock(m.shown.value) }}</span>
          <span>{{ m.known.value ? formatClock(m.duration.value) : '--:--' }}</span>
        </div>
        <div class="mt-4 flex items-center justify-center gap-6">
          <button class="player-btn h-14 w-14" aria-label="Previous" data-testid="now-playing-previous" @click="player.previous()">
            <Icon name="previous" :size="6" />
          </button>
          <button
            class="player-btn player-btn-main h-[72px] w-[72px]"
            :aria-label="m.playing.value ? 'Pause' : 'Play'"
            data-testid="now-playing-toggle"
            @click="m.toggle()"
          >
            <Icon :name="m.playing.value ? 'pause' : 'play'" :size="6" />
          </button>
          <button class="player-btn h-14 w-14" aria-label="Next" :disabled="!player.hasNext" data-testid="now-playing-next" @click="player.next()">
            <Icon name="next" :size="6" />
          </button>
        </div>
        <section v-if="player.upNext.length" class="mt-8" data-testid="up-next">
          <h2 class="label">Up next · {{ player.upNext.length }}</h2>
          <button
            v-for="(i, n) in player.upNext"
            :key="`${i.library}/${i.id}/${n}`"
            class="flex min-h-12 w-full items-center gap-3 border-b border-border text-left"
            @click="player.jump(player.queue.at + 1 + n)"
          >
            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm">{{ i.title }}</span>
              <span class="mono block truncate text-text-muted">{{ i.artist ?? 'Unknown artist' }}</span>
            </span>
          </button>
        </section>
      </div>
    </div>
  </template>

  <div
    v-else-if="cur"
    class="fixed inset-0 z-40 flex items-center justify-center bg-black/95 p-4"
    data-testid="video-overlay"
    @click.self="player.stop()"
  >
    <div class="w-full max-w-4xl">
      <div class="mb-3 flex items-center justify-between border-b border-white/20 pb-2 text-white">
        <h2 class="truncate text-sm tracking-wide" data-testid="now-playing-title">{{ cur.item.title }}</h2>
        <button class="flex h-11 w-11 items-center justify-center text-white/70 hover:text-white" aria-label="Close player" @click="player.stop()">
          <Icon name="close" :size="5" />
        </button>
      </div>
      <MediaElement
        :key="cur.play.url"
        :play="cur.play"
        kind="video"
        autoplay
        :skip="skip"
        :reload="reload"
        @element="onElement"
        @previous="player.previous()"
        @next="player.next()"
      />
    </div>
  </div>
</template>
